// #region Imports

import * as Marklib from '../libs/marklib/modules/Marklib';
import { store } from '../redux/store';
import { actionCreators } from '../redux/modules/document';
import { IDocumentSelection } from '../dto/IDocumentSelection';
import { INoteData } from '../dto/INoteData';

// #endregion Imports

export class SelectorService {
    hasSelection: boolean;
    rootElement: HTMLElement;
    timeout;

    static instance:SelectorService;

    constructor() {
        this.rootElement = document.getElementById('documentcontent-main');
        this.hasSelection = false;
        SelectorService.instance = this;
    }

    initialize(notes:INoteData[]) {
        document.addEventListener('selectionchange', this.onDocumentSelection);
        this.guardAgainstEditing();

        for (let note of notes) {
            if (note.selection == null || note.selection.length === 0) {
                note.elementId = `#${note.markupId}`;
                note.selectionObject = null;
            }
            else {
                this.renderMarking(note);
            }
        }
    }

    private guardAgainstEditing() {
        const editableRoot = document.getElementById('documentcontent');
        if (!editableRoot) {
            return;
        }

        // Descendants of a contenteditable element aren't focusable at all (via Tab or
        // .focus()) unless marked as a non-editable island - so links inside the document
        // body need this to become keyboard-reachable.
        editableRoot.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
            link.setAttribute('contenteditable', 'false');
        });

        editableRoot.addEventListener('beforeinput', (event: InputEvent) => {
            event.preventDefault();
        });

        editableRoot.addEventListener('click', (event: MouseEvent) => {
            const anchor = (event.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null;
            if (!anchor || !editableRoot.contains(anchor)) {
                return;
            }
            const href = anchor.getAttribute('href');
            if (!href || href === '#' || href.startsWith('javascript:')) {
                return;
            }
            window.location.href = anchor.href;
        });

        editableRoot.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Tab') {
                return;
            }

            if (!event.shiftKey && store.getState().document.showTooltip) {
                const firstAction = document.querySelector<HTMLElement>('.tooltip__inner-wrapper .tooltip__action');
                if (firstAction) {
                    event.preventDefault();
                    firstAction.focus();
                    return;
                }
            }

            this.moveFocusWithinDocument(editableRoot, event);
        });
    }

    // Native Tab navigation skips focusable descendants of a contenteditable region, so
    // links and markings inside the document body are otherwise unreachable via keyboard.
    // Both types are gathered from a single query so they stay in actual document order.
    private moveFocusWithinDocument(editableRoot: HTMLElement, event: KeyboardEvent) {
        const items = Array.from(editableRoot.querySelectorAll<HTMLElement>('a[href], [tabindex]'))
            .filter(el => el.tabIndex >= 0 && el.getClientRects().length > 0);
        if (items.length === 0) {
            return;
        }

        const active = document.activeElement;
        const atRoot = active === editableRoot;
        const currentIndex = items.indexOf(active as HTMLElement);

        if (!atRoot && currentIndex === -1) {
            return;
        }

        const step = event.shiftKey ? -1 : 1;
        let nextIndex = atRoot ? (event.shiftKey ? -1 : 0) : currentIndex + step;

        while (nextIndex >= 0 && nextIndex < items.length) {
            const candidate = items[nextIndex];
            candidate.focus();
            if (document.activeElement === candidate) {
                event.preventDefault();
                return;
            }
            nextIndex += step;
        }

        if (!atRoot && this.focusOutsideContainer(editableRoot, step > 0)) {
            event.preventDefault();
        }
    }

    // Re-focusing the container itself and letting the browser's native Tab handling take
    // over doesn't work here: since links/markings are focusable islands inside it, native
    // navigation from the container re-enters its own first descendant instead of exiting.
    // So the next focusable element outside the container is located and focused explicitly.
    private focusOutsideContainer(editableRoot: HTMLElement, forward: boolean): boolean {
        const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
        const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector))
            .filter(el => el.tabIndex >= 0 && el.getClientRects().length > 0 && !editableRoot.contains(el));

        const target = forward
            ? candidates.find(el => !!(editableRoot.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING))
            : [...candidates].reverse().find(el => !!(editableRoot.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING));

        if (!target) {
            return false;
        }
        target.focus();
        return document.activeElement === target;
    }

    private renderMarking = (note: INoteData) => {
        const data = JSON.parse(note.selection);
        const markerId = `marking-${note.id}`;
        
        const marker = new Marklib.Rendering(document, { className: `marking  marking--${note.color}  ${markerId}` }, this.rootElement);
        const result: string = marker.renderWithResult(data);

        if (result == null) {
            // Kunde inte hitta tidigare markering, fallback till id eller dokumentrot
            let target: string = '#documentcontent-main';

            if (note.markupId && note.markupId.length > 0) {
                target = `#${note.markupId}`;
            } else {
                const path = data.startContainerPath;
                const root = this.getElementFromPath(path);
                if (root != null && $(root).length === 1) {
                    target = root;
                }
            }

            note.elementId = target;
            //note.text = `Ursprunglig markering: "${data.text}"\r\n\r\n${note.text || ''}`;
            note.original = `Ursprunglig markering: "${data.text}"`;
            note.fumbled = true;
        }
        else if (result !== data.text) {
            // Texten har ändrats
            $(`x-marker.${markerId}`)
                .first()
                .attr('id', markerId);

            //note.text = `Ursprunglig markering kan ha ändrats: "${data.text}"\r\n\r\n${note.text || ''}`;
            note.original = `Ursprunglig markering kan ha ändrats: "${data.text}"`;
            note.selectionObject = data;
            note.elementId = `#${markerId}`;
        }
        else {
            $(`x-marker.${markerId}`)
                .first()
                .attr('id', markerId);

            note.selectionObject = data;
            note.elementId = `#${markerId}`;
        }
    }

    private getElementFromPath(path: string) {
        if (path.indexOf('>') > 0) {
            return path.substr(0, path.indexOf('>'));
        }

        return null;
    }

    saveSelection = ():INoteData => {
        (window as any).unmark();

        const selection = window.getSelection();
        const marker = new Marklib.Rendering(document, { className: 'marking  marking--new  marking--color4' }, this.rootElement);
        var result = marker.renderWithRange(selection.getRangeAt(0));

        selection.removeAllRanges();
        
        var data = result.serialize();
        data.text = result.text;

        let markupId = this.getElementFromPath(data.startContainerPath);
        if (markupId != null && markupId.startsWith('#')) {
            markupId = markupId.slice(1, markupId.length);
        }

        const note: INoteData = {
            id: '',
            color: 'color4',
            elementId: '',
            markupId: markupId,
            pageId: (window as any).contentId,
            selection: JSON.stringify(data),
            selectionObject: data,
            text: '',
            title: ''
        };

        return note;
    }

    remove = (selector: string) => {
        const element = $(selector).first();
        if (element.length === 0) {
            return;
        }

        Marklib.Rendering.getMarklibInstance(element[0]).destroy();
    }

    getDocumentSelection = (): IDocumentSelection => {
        const selection = window.getSelection();
        let text: string = '';

        for (let i = 0; i < selection.rangeCount; i++) {
            const content = selection.getRangeAt(i).cloneContents();
            const container = document.createElement('div');
            container.appendChild(content);

            text += container.innerHTML;
        }

        const chapterName = this.getChapterFromSelection(selection);

        const result: IDocumentSelection = {
            text: `<article class='xml_document'><div class='xml_content'>${text}</div></article>`,
            chapter: chapterName
        };

        return result;
    }

    private getChapterFromSelection = (selection) => {
        // Hämta upp närmaste rubrik
        const $firstElement = $(selection.anchorNode);
        let chapterName: string = '';

        if ($firstElement.parent('h3').length === 0) {
            // Försök hämta från nivå 2
            chapterName = $firstElement.closest('div.niv2').find('h3.niv2').text();
        }

        if (chapterName === '') {
            // Försök hämta från nivå 1
            chapterName = $firstElement.closest('div.niv1').find('h2.niv1').text();
        }

        return chapterName;
    }

    private onDocumentSelection = () => {
        const selection = window.getSelection();
        clearTimeout(this.timeout);

        if (selection.isCollapsed || !this.isSelectionInsideDocument(selection)) {
            this.hasSelection = false;
            this.timeout = setTimeout(() => { store.dispatch(actionCreators.onDeselect()) }, 100);
            return;
        }

        const rect = selection.getRangeAt(0).getBoundingClientRect();
        const containerLeft = document.getElementById('document-main')?.getBoundingClientRect().left ?? 0;
        const position = { x: rect.left + (rect.width / 2) - containerLeft, y: (rect.bottom + window.pageYOffset) - 150 };

        this.hasSelection = true;
        store.dispatch(actionCreators.onSelect(position));
    }

    private isSelectionInsideDocument(selection: Selection) :boolean {
        return $(selection.anchorNode.parentNode).closest('#documentcontent-main').length === 1 &&
               $(selection.focusNode.parentNode).closest('#documentcontent-main').length === 1;
    }
}